import { wallOpeningOpening3 } from "building-common/openings/openingQuery";
import { getSubMassParentTag, tagBuildingFaces } from "building-types/buildingGeomTag/buildingGeomTags";
import type { BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { CeilingPatchTable } from "building-types/ceilings/ceilingPatchTable";
import { WallType } from "building-types/walls/wallEnums";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { SolidWall, Wall } from "building-types/walls/wallTypes";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { aabb2ByPolyLine2s, aabb2Overlaps } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { aabb3Overlaps, aabb3Union } from "geom/aabb3";
import type { AABB3 } from "geom/aabb3Types";
import { aabb3New } from "geom/aabb3Types";
import type { Face, FaceTagBase } from "geom/brep";
import {
  brepAABB3,
  faceAABB3,
  faceNew,
  faceProjectOuterLoopToPlane,
  faceReverse,
  faceCopy,
  faceMove,
  tagFace,
} from "geom/brep";
import { closestPointPolyLine2 } from "geom/closestPoint";
import { classifyContainmentVec2PolyLine2Fast } from "geom/containment";
import { DIST_EPSILON_SQUARED, WindingDir } from "geom/core";
import { extrudePolyLine2InZ, extrusionToBRep } from "geom/extrude";
import { intersectFaceFace } from "geom/intersect";
import { IntersectionKind } from "geom/intersectRay2";
import { line2MidPt } from "geom/line2";
import { offsetPolyLine2Variable } from "geom/offset";
import { planeCopy, planeNewXY, planeNormal, planeProjectPolyLine3, planeReProjectPolyLine2 } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polygonSubtract } from "geom/polygon";
import {
  polyLine2Area,
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeNormal,
  polyLine2EdgeStart,
  polyLine2End,
  polyLine2HasNaN,
  polyLine2SetCCW,
  polyLine2Start,
  polyLine2WindingDir,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { polyLine3AABB3, polyLine3ByArray, polyLine3OnPlane } from "geom/polyLine3";
import { trimFaceByPlane } from "geom/trim";
import { vec2Dot } from "geom/vec2";
import { iterableSolidWallSideOpenings } from "../elements/walls/iterableSolidWallSideOpenings";
import { getWallPath } from "../elements/walls/wall";
import { performWallCutOnLeftOrRightFace } from "../elements/walls/wallCut";
import { solidWallSubstrateLayer } from "../elements/walls/wallLayers";

// determines how far the center point of a top face edge must be from
//    the wall footprint polyLine in 2D to classify an edge as exposed to the view
const TOP_FACE_EDGE_IS_EXPOSED_DIST_THRESHOLD = 0.001;
const FACE_LOOP_EDGE_OFFSET_FOR_TRIMMING_WALLS = 0.01;
const FACE_LOOP_MAX_DOT_FOR_TRIMMING_WALLS = 0.01;
const TRIM_VERTICAL_OFFSET_TO_TRIM_WALLS_AT_BOTTOM_OF_STAIRS = 0.01;
const VERTICAL_OFFSET_FOR_INTERSECTION_POLYGON = 1000;

const TRIM_WALL_SYSTEM_WITH_STAIRS_TAG: BuildingGeomTag = {
  type: "OPERATION",
  operation: "TRIM_WALL_SYSTEM_WITH_STAIRS",
};

const REPAIRED_STAIR_TRIMMED_WALL_FACE_TAG: BuildingGeomTag = {
  type: "OPERATION",
  operation: "REPAIRED_STAIR_TRIMMED_WALL_FACE",
};

export interface StairBottomOutlineAndPlane {
  readonly plane: Plane;
  readonly outline: PolyLine2;
}

export interface TrimWallSystemWithStairsOptions {
  /**
   * When true, only trim walls/faces whose WALL_SUB_MASS ids appear in
   * `wall.mass.stairClippingOpsBySubMassId` (STAIR_CLIPPING mark path).
   */
  readonly gateByStairClippingOps?: boolean;
}

function allowedSubMassIdsForWall(wall: SolidWall, gateByStairClippingOps: boolean): ReadonlySet<string> | undefined {
  if (!gateByStairClippingOps) {
    return undefined;
  }
  const opsBySubMassId = wall.mass.stairClippingOpsBySubMassId;
  if (!opsBySubMassId || opsBySubMassId.size === 0) {
    return new Set();
  }
  return new Set(opsBySubMassId.keys());
}

function faceAllowedForStairTrim(face: Face, allowedSubMassIds: ReadonlySet<string> | undefined): boolean {
  if (allowedSubMassIds === undefined) {
    return true;
  }
  const subMassTag = getSubMassParentTag(face);
  return subMassTag !== undefined && allowedSubMassIds.has(subMassTag.parentId);
}

export function trimWallSystemWithStairs(
  levelWalls: WallSystem,
  trimFaces: Face[],
  stairBottomOutlinesAndPlanes: StairBottomOutlineAndPlane[],
  ceilingPatchTable: CeilingPatchTable,
  options?: TrimWallSystemWithStairsOptions,
) {
  const gateByStairClippingOps = options?.gateByStairClippingOps === true;

  // this helps ensure the faces at the bottom of the stairs are trimmed properly
  const wallTrimFaces = trimFaces
    .filter((x) => !!x)
    .map((x) => faceMove(faceCopy(x), 0, 0, TRIM_VERTICAL_OFFSET_TO_TRIM_WALLS_AT_BOTTOM_OF_STAIRS));

  for (const id in levelWalls.walls) {
    const wall = levelWalls.walls[id];

    if (wall.wallType !== WallType.SOLID) {
      continue;
    }

    const allowedSubMassIds = allowedSubMassIdsForWall(wall, gateByStairClippingOps);
    if (allowedSubMassIds !== undefined && allowedSubMassIds.size === 0) {
      continue;
    }

    const {
      wallFaces: { leftFaces, rightFaces, topFaces, bottomFaces, startFaces, endFaces },
    } = wall;

    if (topFaces.length === 0 || bottomFaces.length === 0) {
      continue;
    }

    const { leftEdge, rightEdge } = solidWallSubstrateLayer(wall);
    const wallBB = aabb2ByPolyLine2s([leftEdge, rightEdge]);

    for (const leftFace of leftFaces) {
      if (!faceAllowedForStairTrim(leftFace, allowedSubMassIds)) {
        continue;
      }
      trimLeftOrRightWallFacesWithFaceArray(wallTrimFaces, wallBB, leftFace, wall, true, allowedSubMassIds);
    }

    for (const rightFace of rightFaces) {
      if (!faceAllowedForStairTrim(rightFace, allowedSubMassIds)) {
        continue;
      }
      trimLeftOrRightWallFacesWithFaceArray(wallTrimFaces, wallBB, rightFace, wall, false, allowedSubMassIds);
    }

    const [s1x, s1y] = polyLine2Start(rightEdge);
    const [s2x, s2y] = polyLine2End(leftEdge);
    const [e1x, e1y] = polyLine2Start(leftEdge);
    const [e2x, e2y] = polyLine2End(rightEdge);

    for (const { outline, plane } of stairBottomOutlinesAndPlanes) {
      if (
        classifyContainmentVec2PolyLine2Fast(s1x, s1y, outline) ||
        classifyContainmentVec2PolyLine2Fast(s2x, s2y, outline)
      ) {
        const trimmedStartFaces: Face[] = [];
        const errs: string[] = [];
        for (const face of startFaces) {
          if (!faceAllowedForStairTrim(face, allowedSubMassIds)) {
            trimmedStartFaces.push(face);
            continue;
          }
          const tags: FaceTagBase[] = [...(face.tags ?? []), TRIM_WALL_SYSTEM_WITH_STAIRS_TAG];

          const trimmedFace = trimFaceByPlane(face, plane, errs);
          let invalidFace = !trimmedFace.loops.length;
          for (const loop of trimmedFace.loops) {
            if (!loop || polyLine2HasNaN(loop) || polyLine2Area(loop) < DIST_EPSILON_SQUARED) {
              invalidFace = true;
              break;
            }
          }
          if (!invalidFace) {
            const trimmedFaceTagged = tagFace(trimmedFace, tags);
            if (trimmedFaceTagged) {
              trimmedStartFaces.push(trimmedFaceTagged);
            }
          }
        }
        if (errs.length === 0 && trimmedStartFaces) {
          wall.wallFaces.startFaces = [];
          wall.wallFaces.startFaces = trimmedStartFaces;
        }
      }

      if (
        classifyContainmentVec2PolyLine2Fast(e1x, e1y, outline) ||
        classifyContainmentVec2PolyLine2Fast(e2x, e2y, outline)
      ) {
        const trimmedEndFaces: Face[] = [];
        const errs: string[] = [];
        for (const face of endFaces) {
          if (!faceAllowedForStairTrim(face, allowedSubMassIds)) {
            trimmedEndFaces.push(face);
            continue;
          }
          const tags = [...(face.tags ?? []), TRIM_WALL_SYSTEM_WITH_STAIRS_TAG];

          const trimmedFace = trimFaceByPlane(face, plane, errs);
          let invalidFace = !trimmedFace.loops.length;
          for (const loop of trimmedFace.loops) {
            if (!loop || polyLine2HasNaN(loop) || polyLine2Area(loop) < DIST_EPSILON_SQUARED) {
              invalidFace = true;
              break;
            }
          }
          if (!invalidFace) {
            const trimmedFaceTagged = tagFace(trimmedFace, tags);
            if (trimmedFaceTagged) {
              trimmedEndFaces.push(trimmedFaceTagged);
            }
          }
        }
        if (errs.length === 0 && trimmedEndFaces) {
          wall.wallFaces.endFaces = [];
          wall.wallFaces.endFaces = trimmedEndFaces;
        }
      }
    }

    // adjust stair plan outline based on the height of the trim face to ignore parts of the stair
    //   plan outline that are above the top of the wall, when creating new top faces for the wall
    if (wall.trimmedByStairs) {
      const topFacesToRegenerate: Face[] = [];
      const preservedTopFaces: Face[] = [];
      for (const topFace of wall.wallFaces.topFaces) {
        if (faceAllowedForStairTrim(topFace, allowedSubMassIds)) {
          topFacesToRegenerate.push(topFace);
        } else {
          preservedTopFaces.push(topFace);
        }
      }

      if (topFacesToRegenerate.length === 0) {
        continue;
      }

      const worldPlane = planeNewXY();
      const trimOuterLoops: PolyLine2[] = [];
      for (const trimFace of wallTrimFaces) {
        const faceBB = faceAABB3(trimFace);

        if (!wallBB || !aabb2Overlaps(wallBB, faceBB)) {
          continue;
        }

        const topOfWallPlane = topFacesToRegenerate[0].plane;
        const topOfWall = topOfWallPlane.originZ;
        if (faceBB.maxZ <= topOfWall) {
          trimOuterLoops.push(faceProjectOuterLoopToPlane(trimFace, worldPlane));
          continue;
        }

        if (faceBB.minZ >= topOfWall) {
          continue;
        }

        if (faceBB.maxZ > topOfWall) {
          const newFace = trimFaceByPlane(faceCopy(trimFace), topOfWallPlane, []);
          trimOuterLoops.push(faceProjectOuterLoopToPlane(newFace, worldPlane));
        }
      }

      const topFaceLoops: PolyLine2[] = [];
      const topFaceTags: FaceTagBase[][] = [];
      const topFacePlanes: Plane[] = [];
      const topFacesReversed: Face[] = [];

      topFacesToRegenerate.forEach((x) => {
        topFaceLoops.push(faceProjectOuterLoopToPlane(x, worldPlane));
        topFaceTags.push(x.tags ?? []);
        topFacePlanes.push(planeCopy(x.plane));
        topFacesReversed.push(faceReverse(faceCopy(x)));
      });

      const path = getWallPath(wall);
      const leftRoom = path.leftRoom;
      const leftIsInteriorEnclosure = !!leftRoom?.attributes?.roomEnclosure
        ? isInteriorRoomEnclosure[leftRoom.attributes.roomEnclosure]
        : false;

      let roomId: string | undefined;
      if (leftIsInteriorEnclosure) {
        assertIsNotNullOrUndefined(leftRoom); // not sure why this is necessary
        roomId = leftRoom.id;
      } else {
        const rightRoomId = path.rightRoom?.id;
        if (rightRoomId) {
          roomId = path.rightRoom.id;
        } else {
          roomId = path.leftRoom?.id;
        }
      }

      if (roomId && topFacesReversed?.length > 0) {
        const validFaces: Face[] = [];
        for (const face of topFacesReversed) {
          if (!face) {
            continue;
          }
          let invalidFace = !face.loops.length;
          for (const loop of face.loops) {
            if (!loop || polyLine2HasNaN(loop)) {
              invalidFace = true;
              break;
            }
          }
          if (invalidFace) {
            continue;
          }
          validFaces.push(face);
        }

        if (validFaces.length > 0) {
          const roomCeilingPatchFaces = ceilingPatchTable[roomId] || [];
          roomCeilingPatchFaces.push(...topFacesReversed);
          ceilingPatchTable[roomId] = roomCeilingPatchFaces;
        }
      }

      // clear gated topFaces prior to re-adding regenerated topFaces
      wall.wallFaces.topFaces = [...preservedTopFaces];

      for (let i = 0; i < topFaceLoops.length; i++) {
        const topFaceLoop = topFaceLoops[i];
        const topFaceTagArr = topFaceTags[i].filter((x) => !!x); //added filter as a precaution... but should not be necessary
        const tags: FaceTagBase[] = [...topFaceTagArr, TRIM_WALL_SYSTEM_WITH_STAIRS_TAG];
        const footprintResiduals = polygonSubtract([[topFaceLoop], ...trimOuterLoops.map((x) => [x])]);
        for (const item of footprintResiduals) {
          wall.wallFaces.topFaces.push(tagFace(faceNew(item, topFacePlanes[i]), tags));
        }
      }
    }
  }
}

const worldPlaneXY = planeNewXY();

function trimLeftOrRightWallFacesWithFaceArray(
  wallTrimFaces: Face[],
  wallBB: AABB2 | null,
  face: Face,
  wall: SolidWall,
  isLeft: boolean,
  allowedSubMassIds: ReadonlySet<string> | undefined,
) {
  const cutPolys: PolyLine2[] = [];

  // loop through trim faces from stairs and
  for (const trimFace of wallTrimFaces) {
    const errs: string[] = [];
    if (errs.length > 0) {
      continue;
    }

    // this comparison is only using the 2D components of the trim face AABB3
    if (!wallBB || !aabb2Overlaps(wallBB, faceAABB3(trimFace))) {
      continue;
    }

    offsetSomeFaceLoopEdgesForFaceTrim(face, trimFace);

    const intersection = intersectFaceFace(face, trimFace, errs);
    if (intersection?.kind === IntersectionKind.TRANSVERSE) {
      for (const { x0, y0, z0, x1, y1, z1 } of intersection.segments) {
        // create polylines from the intersection segments extended up sufficiently along the z to trim the portion of the wall face above the segment
        cutPolys.push(
          polyLine2SetCCW(
            planeProjectPolyLine3(
              face.plane,
              polyLine3ByArray([
                x0,
                y0,
                z0,
                x1,
                y1,
                z1,
                x1,
                y1,
                z1 + VERTICAL_OFFSET_FOR_INTERSECTION_POLYGON,
                x0,
                y0,
                z0 + VERTICAL_OFFSET_FOR_INTERSECTION_POLYGON,
                x0,
                y0,
                z0,
              ]),
            ),
          ),
        );
      }
    }

    if (cutPolys.length === 0) {
      continue;
    }

    wall.trimmedByStairs = true;

    const { plane } = face;
    for (const poly of cutPolys) {
      performStairWallCutOnLeftOrRightFace(wall.wallFaces, plane, poly, isLeft, allowedSubMassIds);
    }
  }
}

/**
 * Like `performWallCutOnLeftOrRightFace`, but when `allowedSubMassIds` is set only clips
 * faces tagged with those WALL_SUB_MASS ids (STAIR_CLIPPING gate).
 */
function performStairWallCutOnLeftOrRightFace(
  wallFaces: SolidWall["wallFaces"],
  plane: Plane,
  poly: PolyLine2,
  isLeft: boolean,
  allowedSubMassIds: ReadonlySet<string> | undefined,
): void {
  if (allowedSubMassIds === undefined) {
    performWallCutOnLeftOrRightFace(wallFaces, plane, poly, isLeft);
    return;
  }

  const sideFaces = isLeft ? wallFaces.leftFaces : wallFaces.rightFaces;
  const gatedFaces: Face[] = [];
  const ungatedFaces: Face[] = [];
  for (const sideFace of sideFaces) {
    if (faceAllowedForStairTrim(sideFace, allowedSubMassIds)) {
      gatedFaces.push(sideFace);
    } else {
      ungatedFaces.push(sideFace);
    }
  }
  if (gatedFaces.length === 0) {
    return;
  }

  if (isLeft) {
    wallFaces.leftFaces = gatedFaces;
  } else {
    wallFaces.rightFaces = gatedFaces;
  }
  performWallCutOnLeftOrRightFace(wallFaces, plane, poly, isLeft);
  if (isLeft) {
    wallFaces.leftFaces = [...ungatedFaces, ...wallFaces.leftFaces];
  } else {
    wallFaces.rightFaces = [...ungatedFaces, ...wallFaces.rightFaces];
  }
}

/**
 * Collinear trim-face edge indices whose endpoints lie on `face`'s supporting plane
 * (wall-relative XY), using the same tolerance as v1 stair wall trim.
 */
function faceTrimCollinearEdgeIndices(
  face: Face,
  loopInWorldXYPlane: ReturnType<typeof planeReProjectPolyLine2>,
): number[] {
  const [facenx, faceny] = planeNormal(face.plane);
  const { originX, originY } = face.plane;
  const edgeIndices: number[] = [];
  for (let i = 0; i < polyLine2EdgeCount(loopInWorldXYPlane); i++) {
    const [sx, sy] = polyLine2EdgeStart(loopInWorldXYPlane, i);
    const [ex, ey] = polyLine2EdgeEnd(loopInWorldXYPlane, i);
    const startDot = vec2Dot(facenx, faceny, sx - originX, sy - originY);
    if (Math.abs(startDot) > FACE_LOOP_MAX_DOT_FOR_TRIMMING_WALLS) {
      continue;
    }
    const endDot = vec2Dot(facenx, faceny, ex - originX, ey - originY);
    if (Math.abs(endDot) > FACE_LOOP_MAX_DOT_FOR_TRIMMING_WALLS) {
      continue;
    }
    edgeIndices.push(i);
  }
  return edgeIndices;
}

/**
 * Offsets the edges of a face loop to trim walls faces or ignore them based on
 * whether it overlaps the wall or just butts against the edge.
 */
function offsetSomeFaceLoopEdgesForFaceTrim(face: Face, trimFace: Face) {
  const [facenx, faceny] = planeNormal(face.plane);

  for (const loopIndex of Object.keys(trimFace.loops)) {
    const loop = trimFace.loops[loopIndex];
    const loopInWorldXYPlane = planeReProjectPolyLine2(loop, trimFace.plane, worldPlaneXY);

    const edgeIndices = faceTrimCollinearEdgeIndices(face, loopInWorldXYPlane);
    if (edgeIndices.length === 0) {
      continue;
    }

    // check winding dir of original face loop, since reprojected loops obfuscate the original winding dir
    const windingDir = polyLine2WindingDir(loop);

    // create array of offsets for each edge in the loop
    const edgeCount = polyLine2EdgeCount(loopInWorldXYPlane);
    const offsets: number[] = new Array<number>(edgeCount).fill(0);

    let offsetsAdded = false;
    for (const edgeIndex of edgeIndices) {
      const [edgenx, edgeny] = polyLine2EdgeNormal(loopInWorldXYPlane, edgeIndex);

      // since we already tested for collinearity, we don't need to be too strict here with 1 - dist epsilon, we can use 0.
      // we should be dealing with either a 0 or 180 degree angle between the edge and the face normal
      if (vec2Dot(facenx, faceny, edgenx, edgeny) < 0) {
        // ensure opposite facing scenarios offset inward and don't cut into the wall
        offsets[edgeIndex] =
          windingDir === WindingDir.CW
            ? FACE_LOOP_EDGE_OFFSET_FOR_TRIMMING_WALLS
            : -FACE_LOOP_EDGE_OFFSET_FOR_TRIMMING_WALLS;
        offsetsAdded = true;
        continue;
      }

      offsetsAdded = true;

      // we need to know the winding dir of the original face loop to determine which direction to offset outwards
      offsets[edgeIndex] =
        windingDir === WindingDir.CW
          ? -FACE_LOOP_EDGE_OFFSET_FOR_TRIMMING_WALLS
          : FACE_LOOP_EDGE_OFFSET_FOR_TRIMMING_WALLS;
    }

    if (!offsetsAdded) {
      continue;
    }

    trimFace.loops[loopIndex] = offsetPolyLine2Variable(loop, offsets);
  }
}

const MINIMUM_WALL_TOP_FACE_AREA_FOR_REPAIR = 1;

export function repairStairTrimmedWallFaces(levelWalls: WallSystem, options?: TrimWallSystemWithStairsOptions) {
  const gateByStairClippingOps = options?.gateByStairClippingOps === true;

  for (const id in levelWalls.walls) {
    const wall = levelWalls.walls[id] as Wall;

    if (wall.wallType !== WallType.SOLID) {
      continue;
    }

    if (!wall.trimmedByStairs) {
      continue;
    }

    const allowedSubMassIds = allowedSubMassIdsForWall(wall, gateByStairClippingOps);
    if (allowedSubMassIds !== undefined && allowedSubMassIds.size === 0) {
      continue;
    }

    const { footprint } = solidWallSubstrateLayer(wall);
    if (!footprint) {
      continue;
    }

    if (wall.wallFaces.bottomFaces.length === 0 || wall.wallFaces.topFaces.length === 0) {
      continue;
    }

    const {
      mass: { floorElevation },
      leftSide: leftWallSide,
      rightSide: rightWallSide,
    } = wall;
    const {
      baseOffset,
      wallDescriptor: { leftSide, rightSide },
    } = getWallPath(wall);
    // this will not catch all wall vertical conditions, but
    // doing that will require a full re-write of this function
    const bottomOfWall = floorElevation + baseOffset;
    const topOfWall = Math.max(leftSide.wallSideDatums.wallTop, rightSide.wallSideDatums.wallTop);

    const tags: BuildingGeomTag[] = [
      {
        type: "PARENT",
        parentId: id,
        parentType: wall.entityType,
      },
      REPAIRED_STAIR_TRIMMED_WALL_FACE_TAG,
    ];

    let wallHasOpeningAABB3 = false;
    let wallOpeningAABB3 = aabb3New(
      -Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
      -Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
      -Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
    );

    for (const { opening } of iterableSolidWallSideOpenings(leftWallSide)) {
      if (!opening) {
        continue;
      }
      const pl = wallOpeningOpening3(opening, "FRONT", false);
      if (!pl) {
        continue;
      }
      wallOpeningAABB3 = aabb3Union(wallOpeningAABB3, polyLine3AABB3(pl));
      wallHasOpeningAABB3 = true;
    }
    for (const { opening } of iterableSolidWallSideOpenings(rightWallSide)) {
      if (!opening) {
        continue;
      }
      const pl = wallOpeningOpening3(opening, "FRONT", false);
      if (!pl) {
        continue;
      }
      wallOpeningAABB3 = aabb3Union(wallOpeningAABB3, polyLine3AABB3(pl));
      wallHasOpeningAABB3 = true;
    }

    // if the exposed edge face overlaps any wall hole faces (e.g. doors,windows ), stop the face at the highest overlap z-height
    for (const face of wall.wallFaces.topFaces) {
      if (!faceAllowedForStairTrim(face, allowedSubMassIds)) {
        continue;
      }
      const area = polyLine2Area(face.loops[0]);
      if (Number.isNaN(area) || area < MINIMUM_WALL_TOP_FACE_AREA_FOR_REPAIR) {
        continue;
      }
      const pl3 = polyLine3OnPlane(face.loops[0], face.plane);

      for (let i = 0, l = pl3.length - 3; i < l; i += 3) {
        const sx = pl3[i];
        const sy = pl3[i + 1];
        const ex = pl3[i + 3];
        const ey = pl3[i + 4];

        const [cX, cY] = line2MidPt(sx, sy, ex, ey);
        const { dist } = closestPointPolyLine2(footprint, cX, cY);

        if (dist > TOP_FACE_EDGE_IS_EXPOSED_DIST_THRESHOLD) {
          const topFaceExposedEdgeExtrusion = extrusionToBRep(
            extrudePolyLine2InZ(polyLine2New([sx, sy, ex, ey, sx, sy]), bottomOfWall, topOfWall),
          );

          /**
           * @TODO @jaggyluis this method will not work for tagged side faces.
           *
           * These newly created faces do not have the correct tags from the wall
           * sections that created the side faces. This will need to be
           * reworked before side faces can be tagged
           */

          if (!wallHasOpeningAABB3) {
            // if not trimmed use full height edge extrusion
            wall.wallFaces.rightFaces.push(...tagBuildingFaces(topFaceExposedEdgeExtrusion.faces, tags));
          }

          const sideFaceBB = brepAABB3(topFaceExposedEdgeExtrusion);
          let maxIntersectionZ = bottomOfWall;
          let topFaceAABB3OverlapsHoleFaceAABB3 = false;

          const checkAABB3Overlap = (aabb3: AABB3) => {
            if (aabb3Overlaps(sideFaceBB, aabb3)) {
              const maxZ = aabb3.maxZ;
              if (maxZ > maxIntersectionZ) {
                maxIntersectionZ = maxZ;
                topFaceAABB3OverlapsHoleFaceAABB3 = true;
              }
            }
          };

          checkAABB3Overlap(wallOpeningAABB3);

          if (topFaceAABB3OverlapsHoleFaceAABB3) {
            const extrusionTrimmed = extrusionToBRep(
              extrudePolyLine2InZ(
                polyLine2New([sx, sy, ex, ey, sx, sy]),
                maxIntersectionZ,
                topOfWall - maxIntersectionZ,
              ),
            );
            wall.wallFaces.rightFaces.push(...tagBuildingFaces(extrusionTrimmed.faces, tags));
          }
        }
      }
    }
  }
}
